import { useState } from 'react';
import TimeInQueue from './TimeInQueue';
import { Icon, BottomSheet } from '../../mobile';

// Phone call card (Sep 8 mobile rebuild). One glance: status, how long it
// has waited, where it is, what it is. One primary action: Claim when it is
// open, Resolve when it is mine. Everything else (Unclaim, Edit, Delete)
// sits behind the options button so a thumb never lands on Delete by
// accident. Tap the body to open the call.
const STATUS = {
  Open: { bg: '#eff6ff', fg: '#2563eb' },
  Claimed: { bg: '#fffbeb', fg: '#b45309' },
  Resolved: { bg: '#f0fdf4', fg: '#15803d' },
};

export default function CallCardMobile({ call, user, can, canInModule, onClaim, onUnclaim, onResolve, onEdit, onDelete, onClick }) {
  const [options, setOptions] = useState(false);
  const isDriver = (user.team_name || '') === 'Driver Operations' || user.role === 'driver';
  const isMyClaim = call.claimed_by === user.id;
  const canUnclaim = call.status === 'Claimed' && (isMyClaim || !isDriver);
  const canEdit = (canInModule ? canInModule('dispatch', 'edit_calls') : can('edit_calls')) && call.status !== 'Resolved';
  const canDelete = can('delete_calls');
  const isDueTimePending = call.due_time && new Date(call.due_time.replace(' ', 'T') + 'Z').getTime() > Date.now();
  const dueTimeDisplay = call.due_time
    ? new Date(call.due_time.replace(' ', 'T') + 'Z').toLocaleTimeString('en-US', { timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit' })
    : null;
  const canResolve = (call.status === 'Claimed' && (isMyClaim || can('resolve_calls')))
    || (call.status === 'Open' && !isDriver && can('resolve_calls'));
  const canClaim = call.status === 'Open' && can('claim_calls');
  const hasOptions = canUnclaim || canEdit || canDelete;
  const s = STATUS[call.status] || STATUS.Open;
  const stop = fn => e => { e.stopPropagation(); fn(); };

  return (
    <div className={`m-card${call.is_emergency ? ' m-card-emergency' : ''}`} onClick={onClick} role="button" tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter') onClick(); }} style={{ cursor: 'pointer', padding: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '12px 14px 0' }}>
        <span className="m-pill" style={{ background: s.bg, color: s.fg }}>{call.status.toUpperCase()}</span>
        {call.is_emergency ? <span className="m-pill" style={{ background: '#fef2f2', color: '#dc2626' }}>EMERGENCY</span> : null}
        {call.call_type_name && <span className="m-pill" style={{ background: 'var(--bg-inset)', color: 'var(--text-secondary)' }}>{call.call_type_name}</span>}
        <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 700, color: '#b45309', fontVariantNumeric: 'tabular-nums' }}>
          <TimeInQueue createdAt={call.due_time || call.created_at} />
        </span>
      </div>
      <div style={{ padding: '10px 14px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div className="m-card-title">{call.property_name || call.call_type_name || 'Call'}</div>
        <div className="m-card-meta">{call.location}</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
          {call.space_number && <span>Space <strong>#{call.space_number}</strong></span>}
          {call.vehicle_info && <span>{call.vehicle_info}</span>}
          {dueTimeDisplay && <span style={{ color: '#b45309', fontWeight: 600 }}>Due {dueTimeDisplay} CT</span>}
          {call.claimed_by_name && <span>{isMyClaim ? 'Mine' : call.claimed_by_name}</span>}
          {call.comment_count > 0 && <span>{call.comment_count} message{call.comment_count === 1 ? '' : 's'}</span>}
        </div>
      </div>
      {(canClaim || canResolve || hasOptions) && (
        <div style={{ display: 'flex', gap: 8, padding: '0 14px 14px' }} onClick={e => e.stopPropagation()}>
          {canClaim && (
            <button type="button" className="m-btn" onClick={stop(() => onClaim(call.id))}
              style={{ background: 'var(--brand-accent)', color: '#fff', height: 48, flex: 1 }}>Claim</button>
          )}
          {canResolve && (isDueTimePending ? (
            <button type="button" className="m-btn m-btn-secondary" disabled style={{ flex: 1 }}>Due at {dueTimeDisplay}</button>
          ) : (
            <button type="button" className="m-btn" onClick={stop(() => onResolve(call))}
              style={{ background: '#16a34a', color: '#fff', height: 48, flex: 1 }}>
              <Icon name="check" size={20} color="#fff" /> Resolve
            </button>
          ))}
          {!canClaim && !canResolve && <div style={{ flex: 1 }} />}
          {hasOptions && (
            <button type="button" className="m-btn-icon" aria-label="Options" onClick={stop(() => setOptions(true))} style={{ width: 48, height: 48 }}>
              <Icon name="more" size={22} />
            </button>
          )}
        </div>
      )}
      <BottomSheet open={options} title={call.property_name || `Call #${call.id}`} subtitle={call.location} onClose={() => setOptions(false)}>
        <div className="m-list" onClick={e => e.stopPropagation()}>
          {canUnclaim && <button type="button" className="m-btn m-btn-secondary" onClick={() => { setOptions(false); onUnclaim(call.id); }}>Unclaim</button>}
          {canEdit && <button type="button" className="m-btn m-btn-secondary" onClick={() => { setOptions(false); onEdit(call); }}>Edit call</button>}
          {canDelete && (
            <button type="button" className="m-btn m-btn-secondary" style={{ color: '#dc2626', borderColor: '#fecaca' }}
              onClick={() => { setOptions(false); onDelete(call.id); }}>Delete call</button>
          )}
        </div>
      </BottomSheet>
    </div>
  );
}
